import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChatKeys } from "web_myplann/Api/Chat/ChatKeys";
import {
	appendConfirmedChatMessage,
	appendOptimisticChatMessage,
	markChatMessageFailed,
	replaceOptimisticChatMessage,
	seedOptimisticChatMessagesCache,
} from "web_myplann/Chat/Helpers/chatMessagesCache";
import { createOptimisticChatMessage } from "web_myplann/Chat/Helpers/createOptimisticChatMessage";
import { mapChatMessage } from "web_myplann/Chat/Helpers/mapChatMessage";
import type { ChatMessageView } from "web_myplann/Chat/Types/chatMessage";
import {
	postChatAdd,
	postChatAddFirst,
} from "web_myplann/Infrastructure/ApiOld/Api";

type SendMessageVariables = {
	conversationId: number | null;
	advisorId?: string;
	content: string;
	clientId: string;
};

type SendMessageResult = {
	message: ChatMessageView;
	conversationId: number;
};

export const useSendMessageMutation = (conversationId: number | null) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: ChatKeys.send(conversationId),
		scope: { id: ChatKeys.sendScope(conversationId) },
		mutationFn: async ({
			conversationId,
			advisorId,
			content,
		}: SendMessageVariables): Promise<SendMessageResult> => {
			const response =
				conversationId !== null
					? await postChatAdd({ conversationId, content })
					: await postChatAddFirst({
							advisorIds: advisorId ? [advisorId] : [],
							content,
						});

			if (response.status !== 200) {
				throw new Error("Failed to send message");
			}

			const item = response.data.data.item;

			return {
				message: mapChatMessage(item),
				conversationId: item.chat_conversation,
			};
		},
		onMutate: ({ conversationId, content, clientId }) => {
			const message = createOptimisticChatMessage({ clientId, content });

			if (conversationId === null) {
				seedOptimisticChatMessagesCache(queryClient, message);
			} else {
				appendOptimisticChatMessage(queryClient, conversationId, message);
			}

			return { conversationId, clientId };
		},
		onError: (_error, _variables, context) => {
			if (!context) return;
			markChatMessageFailed(
				queryClient,
				context.conversationId,
				context.clientId,
			);
		},
		onSuccess: (result, _variables, context) => {
			const replaced = replaceOptimisticChatMessage(
				queryClient,
				context.conversationId,
				context.clientId,
				result.message,
			);

			if (!replaced) {
				appendConfirmedChatMessage(
					queryClient,
					context.conversationId,
					result.message,
				);
			}

			if (context.conversationId === null) {
				queryClient.removeQueries({ queryKey: ChatKeys.messages(null) });
			}

			queryClient.invalidateQueries({ queryKey: ChatKeys.list() });
		},
	});
};
